/**
 * @module components/QuickView
 * @description Brief §4.5: pop-up to review a cause without leaving the list:
 * image gallery, impact, funding progress and goal, preset/custom amounts,
 * add to cart and keep browsing.
 *
 * Built on the native <dialog> (showModal): focus trap, Esc-to-close and
 * inert background come from the browser, not hand-rolled code.
 */
import { useEffect, useRef, useState, type JSX } from "react";
import { useGiving } from "../app/GivingContext";
import { useCauseActions } from "../app/useCauseActions";
import { DEFAULT_GIFT_USD } from "../config/page";
import { CATEGORY_LABELS, PARISH_LABELS } from "../config/taxonomy";
import type { Cause, CartLine, MediaImage } from "../domain/types";
import { formatUsd } from "../lib/format";
import { AmountPicker } from "./AmountPicker";
import { UrgencyBadge } from "./CauseCard";
import { Icon } from "./Icons";
import { Progress } from "./Progress";
import "./QuickView.css";

/** Quick View dialog; open when GivingContext.quickViewId is set. */
export function QuickView(): JSX.Element {
    const { quickViewId, openQuickView, causeById, lines, setCartOpen } = useGiving();
    const actions = useCauseActions();
    const dialog = useRef<HTMLDialogElement>(null);
    const cause: Cause | undefined = quickViewId === null ? undefined : causeById(quickViewId);
    const existing: CartLine | undefined = lines.find((l: CartLine) => l.causeId === quickViewId);
    const [amount, setAmount] = useState<number>(DEFAULT_GIFT_USD);
    const [imageIndex, setImageIndex] = useState<number>(0);

    // Sync the native dialog with state; reset the form for each new cause.
    useEffect(() => {
        const el: HTMLDialogElement | null = dialog.current;
        if (el === null) return;
        if (cause !== undefined && !el.open) {
            setAmount(existing?.amountUsd ?? DEFAULT_GIFT_USD);
            setImageIndex(0);
            el.showModal();
        } else if (cause === undefined && el.open) {
            el.close();
        }
    }, [cause, existing?.amountUsd]);

    const close = (): void => openQuickView(null);
    const images: MediaImage[] = cause === undefined ? [] : [cause.image, ...cause.gallery];

    return (
        <dialog ref={dialog} className="quick-view" onClose={close} onClick={(e) => { if (e.target === dialog.current) close(); }} aria-labelledby="qv-title">
            {cause !== undefined && (
                <div className="quick-view__inner">
                    <div className="quick-view__gallery">
                        <img src={images[imageIndex]?.src} alt={images[imageIndex]?.alt ?? ""} className="quick-view__image" />
                        {images.length > 1 && (
                            <div className="quick-view__thumbs">
                                {images.map((img: MediaImage, i: number) => (
                                    <button key={img.src} type="button" aria-pressed={i === imageIndex} onClick={() => setImageIndex(i)} aria-label={`Photo ${i + 1}`}>
                                        <img src={img.src} alt="" />
                                    </button>
                                ))}
                            </div>
                        )}
                    </div>

                    <div className="quick-view__body">
                        <button type="button" className="icon-btn quick-view__close" onClick={close} aria-label="Close quick view"><Icon name="close" /></button>
                        <div className="quick-view__badges">
                            <span className="badge">{CATEGORY_LABELS[cause.category]}</span>
                            <UrgencyBadge cause={cause} />
                            <span className="badge badge--sample">{PARISH_LABELS[cause.parish]}</span>
                        </div>
                        <p className="cause-card__partner">{cause.partner}</p>
                        <h2 id="qv-title" className="quick-view__title">{cause.title}</h2>
                        <p>{cause.summary}</p>

                        <div className="quick-view__impact">
                            <span className="quick-view__impact-value">{cause.impact.value}</span>
                            <span>{cause.impact.label}<small> · AFJ-reported impact</small></span>
                        </div>

                        <Progress funding={cause.funding} />
                        <AmountPicker value={amount} onChange={setAmount} />

                        <div className="quick-view__actions">
                            <button type="button" className="btn btn--primary btn--lg" onClick={() => { actions.addToCart(cause, amount); close(); }}>
                                <Icon name="cart" /> {existing ? "Update cart" : "Add to Cart"} · {formatUsd(amount)}
                            </button>
                            <button type="button" className="btn btn--ghost btn--lg" onClick={() => { actions.addToCart(cause, amount); close(); setCartOpen(true); }}>
                                Add and view cart
                            </button>
                        </div>
                        <button type="button" className="quick-view__keep" onClick={close}>Keep browsing</button>
                    </div>
                </div>
            )}
        </dialog>
    );
}
